import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label as UILabel } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Plus, ExternalLink, Search, FileText } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { v: "general", l: "General" }, { v: "project_doc", l: "Project Doc" },
  { v: "technical_doc", l: "Technical" }, { v: "requirement", l: "Requirement" },
  { v: "sop", l: "SOP" }, { v: "meeting_notes", l: "Meeting Notes" },
  { v: "decision", l: "Decision" }, { v: "release_notes", l: "Release Notes" },
  { v: "cr_doc", l: "CR Doc" }, { v: "knowledge_base", l: "Knowledge Base" }, { v: "policy", l: "Policy" },
];
const typeLabel = (v) => DOC_TYPES.find((t) => t.v === v)?.l || "General";

/**
 * Reusable "Documentation" panel that drops into any detail page.
 * Lists live docs linked to the given PRIZM entity + lets the user attach
 * an existing doc or spin up a new one (optionally from a template).
 */
export default function LinkedDocsTab({ entityType, entityId, title = "Documentation" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/by-entity/${entityType}/${entityId}`);
      setDocs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (entityId) load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const detach = async (link_id, doc_id) => {
    if (!window.confirm("Unlink this document from this item?")) return;
    try { await api.delete(`/documents/${doc_id}/links/${link_id}`); toast.success("Unlinked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid={`linked-docs-${entityType}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-medium text-sm flex items-center gap-1.5"><BookOpen className="w-4 h-4" />{title}</div>
          <div className="text-xs text-muted-foreground">Knowledge docs attached to this {entityType.replace("_", " ")}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setAttachOpen(true)} data-testid={`attach-doc-${entityType}`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Attach existing
          </Button>
          <Button size="sm" onClick={() => setNewOpen(true)} data-testid={`new-linked-doc-${entityType}`}>
            <Plus className="w-3.5 h-3.5 mr-1" />New doc here
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border/60 rounded-lg">
          No linked docs yet. Capture decisions, architecture or runbooks here.
        </div>
      ) : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} className="glass rounded-md px-3 py-2 flex items-center gap-2" data-testid={`linked-doc-row-${d.id}`}>
              <div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center text-sm shrink-0">
                {d.icon || "📄"}
              </div>
              <RouterLink to={`/documentation/${d.id}`} className="flex-1 min-w-0 text-sm font-medium hover:text-primary truncate">
                {d.title}
              </RouterLink>
              <Badge variant="outline" className="text-[9px] shrink-0">{typeLabel(d.doc_type)}</Badge>
              {d.status && d.status !== "draft" && (
                <Badge variant="outline" className="text-[9px] capitalize shrink-0">{d.status.replace("_", " ")}</Badge>
              )}
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => nav(`/documentation/${d.id}`)}>
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <NewDocHereDialog
        open={newOpen} onOpenChange={setNewOpen}
        entityType={entityType} entityId={entityId}
        onDone={load}
      />
      <AttachExistingDialog
        open={attachOpen} onOpenChange={setAttachOpen}
        entityType={entityType} entityId={entityId}
        linkedIds={docs.map((d) => d.id)}
        onDone={load}
      />
    </div>
  );
}

function NewDocHereDialog({ open, onOpenChange, entityType, entityId, onDone }) {
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("general");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    api.get("/documents?tab=templates").then((r) => setTemplates(r.data));
    setTitle(""); setDocType("general"); setTemplateId("");
  }, [open]);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { title: title.trim() || "Untitled", doc_type: docType };
      if (templateId) payload.template_id = templateId;
      const { data } = await api.post(`/documents/by-entity/${entityType}/${entityId}`, payload);
      toast.success("Document created & linked");
      onOpenChange(false); onDone?.();
      nav(`/documentation/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`new-linked-doc-dialog-${entityType}`}>
        <DialogHeader>
          <DialogTitle>New Document — linked here</DialogTitle>
          <DialogDescription>Create a doc that's auto-linked to this {entityType.replace("_", " ")}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <UILabel>Title</UILabel>
            <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Untitled" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <UILabel>Type</UILabel>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{DOC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <UILabel>Template</UILabel>
              <Select value={templateId || "blank"} onValueChange={(v) => setTemplateId(v === "blank" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="blank">Blank</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={create} disabled={saving}>{saving ? "Creating…" : "Create & open"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachExistingDialog({ open, onOpenChange, entityType, entityId, linkedIds, onDone }) {
  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/documents?tab=home").then((r) => setAll(r.data));
    setQ("");
  }, [open]);
  const filtered = all.filter((d) => !linkedIds.includes(d.id) && d.title.toLowerCase().includes(q.toLowerCase())).slice(0, 60);

  const attach = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/links`, { entity_type: entityType, entity_id: entityId, relation_type: "related" });
      toast.success("Attached");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Attach an existing document</DialogTitle>
          <DialogDescription>Pick a doc from your workspace to link to this {entityType.replace("_", " ")}.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by title…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1 mt-2">
          {filtered.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No matching unlinked docs.</div>}
          {filtered.map((d) => (
            <button
              key={d.id}
              onClick={() => attach(d)}
              className="w-full glass rounded-md px-3 py-2 flex items-center gap-2 hover:border-primary/40 text-left"
            >
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="flex-1 min-w-0 text-sm truncate">{d.title}</span>
              <Badge variant="outline" className="text-[10px]">{typeLabel(d.doc_type)}</Badge>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
